import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';

export default function ISectionProperties({ section }) {
  if (!section) return null;

  const { properties } = section;

  return (
    <TableContainer>
      <Table size="small">
        <TableBody>
          {/* General Properties */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">
                General Properties
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Fabrication</TableCell>
            <TableCell>{properties?.fabrication || ''}</TableCell>
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell component="th">Availability</TableCell>
            <TableCell>{properties?.availability || ''}</TableCell>
            <TableCell />
          </TableRow>

          {/* Shape Details */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">Shape Details</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">d (Total Depth)</TableCell>
            <TableCell>{properties?.d || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">bf (Flange Width)</TableCell>
            <TableCell>{properties?.bf || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">tf (Flange Thickness)</TableCell>
            <TableCell>{properties?.tf || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">tw (Web Thickness)</TableCell>
            <TableCell>{properties?.tw || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">r (Root Radius)</TableCell>
            <TableCell>{properties?.r1 || '0'}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>

          {/* Strength Properties */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">Strength Properties</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">fy (Normal Strength)</TableCell>
            <TableCell>{properties?.fy || ''}</TableCell>
            <TableCell>MPa</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">fyw (Web Normal Strength)</TableCell>
            <TableCell>{properties?.fyw || ''}</TableCell>
            <TableCell>MPa</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">fu (Ultimate Strength)</TableCell>
            <TableCell>{properties?.fu || ''}</TableCell>
            <TableCell>MPa</TableCell>
          </TableRow>

          {/* Section Properties */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">Section Properties</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Ag (Cross-sectional Area)</TableCell>
            <TableCell>{properties?.ag || ''}</TableCell>
            <TableCell>mm²</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Ix (Second Moment of Area X)</TableCell>
            <TableCell>{properties?.ix ? (properties.ix / 1e6) : ''}</TableCell>
            <TableCell>×10⁶ mm⁴</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Zx (Elastic Section Modulus X)</TableCell>
            <TableCell>{properties?.zx ? (properties.zx / 1e3) : ''}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Sx (Plastic Section Modulus X)</TableCell>
            <TableCell>{properties?.sx ? (properties.sx / 1e3) : ''}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">rx (Radius of Gyration X)</TableCell>
            <TableCell>{properties?.rx || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Zex (Effective Section Modulus X)</TableCell>
            <TableCell>{properties?.zex}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>

          {/* Y-Axis Properties */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">About Y-Axis Properties</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Iy (Second Moment of Area Y)</TableCell>
            <TableCell>{properties?.iy ? (properties.iy / 1e6) : ''}</TableCell>
            <TableCell>×10⁶ mm⁴</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Zy (Elastic Section Modulus Y)</TableCell>
            <TableCell>{properties?.zy ? (properties.zy / 1e3) : ''}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Sy (Plastic Section Modulus Y)</TableCell>
            <TableCell>{properties?.sy ? (properties.sy / 1e3) : ''}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">ry (Radius of Gyration Y)</TableCell>
            <TableCell>{properties?.ry || ''}</TableCell>
            <TableCell>mm</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Zey (Effective Section Modulus Y)</TableCell>
            <TableCell>{properties?.zey}</TableCell>
            <TableCell>×10³ mm³</TableCell>
          </TableRow>

          {/* Additional Properties */}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" fontWeight="bold">Additional Properties</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">J (Torsion Constant)</TableCell>
            <TableCell>{properties?.j ? (properties.j / 1e3) : ''}</TableCell>
            <TableCell>×10³ mm⁴</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Iw (Warping Constant)</TableCell>
            <TableCell>{properties?.iw ? (properties.iw / 1e9) : ''}</TableCell>
            <TableCell>×10⁹ mm⁶</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Form Factor</TableCell>
            <TableCell>{properties?.kf?.toFixed(2) || ''}</TableCell>
            <TableCell></TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Compactness about X</TableCell>
            <TableCell>{properties?.compactnessX || ''}</TableCell>
            <TableCell></TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Compactness about Y</TableCell>
            <TableCell>{properties?.compactnessY || ''}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

